import { Transition } from '@headlessui/react';
import { CheckCircleIcon, InformationCircleIcon, StarIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { ReactNode, useState } from 'react';

import { Button } from '../../ui/components/Button';
import { AccountResponse } from '../types';
import { useAppSelector } from '../../../store';
import { PasswordChangeForm } from './PasswordChangeForm';
import { EmailChangeForm } from './EmailChangeForm';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';

type Props = {
  account: AccountResponse
}

type FeatureProps = {
  children: ReactNode[]
}

const Feature = ({ children }: FeatureProps) => (
  <div className='grid grid-cols-subgrid col-span-2 items-center'>
    { children }
  </div>
);

const SyncEnabled = () => (
  <Feature>
    <CheckCircleIcon className='text-green-500 size-8' />
    <span>Your library will sync between computers</span>
  </Feature>
);

const SyncDisabled = () => (
  <Feature>
    <XCircleIcon className='text-red-500 size-8' />
    <span>Your library will not sync between computers</span>
  </Feature>
);

const AccountUnlimited = () => (
  <Feature>
    <CheckCircleIcon className='text-green-500 size-8' />
    <span>You can store unlimited songs and liturgy</span>
  </Feature>
);

type AccountLimitedProps = {
  resourceLimit: number
}

const AccountLimited = ({ resourceLimit }: AccountLimitedProps) => {
  const resources = useAppSelector(state => state.resources);
  const resourceCount = Object.keys(resources).length;
  return (
    <Feature>
      <InformationCircleIcon className='text-jw-blue size-8' />
      <span>
        You may store up to <b>{resourceLimit}</b> songs and liturgy. You are
        currently storing { ' ' }
        <b>
          {resourceCount} item
          { resourceCount === 1 ? '' : 's' }
        </b>.
      </span>
    </Feature>
  );
};

type Pane = 'main' | 'email' | 'password';

type Message = {
  isSuccess: boolean,
  text: string
}

export const AccountDetails = ({ account }: Props) => {
  const [activePane, setActivePane] = useState<Pane>('main');
  const [message, setMessage] = useState<Message>();

  const createSuccessHandler = (text: string) => () => {
    setMessage({ text, isSuccess: true });
    setActivePane('main');
  };

  const failureHandler = (errors: FetchBaseQueryError | SerializedError) => {
    // TODO better error messages
    setMessage({ isSuccess: false, text: `An error occurred: ${'status' in errors ? errors.status : errors.message}` });
    setActivePane('main');
  };

  return (
    <div className='grow flex flex-col relative h-full'>
      <Pane show={activePane === 'main'}>
        <MainPane
          account={account}
          message={message}
          setActivePane={setActivePane}
        />
      </Pane>
      <Pane show={activePane === 'email'}>
        <EmailChangeForm
          onFailure={failureHandler}
          onSuccess={createSuccessHandler('Email has been changed successfully.')}
        />
        <Button
          className='self-end'
          grow={false}
          onClick={() => setActivePane('main')}
        >
          Cancel
        </Button>
      </Pane>
      <Pane show={activePane === 'password'}>
        <PasswordChangeForm
          onFailure={failureHandler}
          onSuccess={createSuccessHandler('Password has been changed successfully.')}
        />
        <Button
          className='self-end'
          grow={false}
          onClick={() => setActivePane('main')}
        >
          Cancel
        </Button>
      </Pane>
    </div>
  );
};

type PaneProps = {
  children: ReactNode | ReactNode[],
  show?: boolean
}

const Pane = ({ children, show }: PaneProps) => (
  <Transition show={show}>
    <div className='transition duration-100 ease-in data-[closed]:opacity-0 grow flex flex-col h-full w-full absolute justify-evenly'>
      { children }
    </div>
  </Transition>
);

type MainPaneProps = Props & {
  message?: Message,
  setActivePane: (pane: Pane) => void
}

const MainPane = ({ account, message, setActivePane }: MainPaneProps) => {
  const canSync = account.permissions.includes('SYNC');
  const unlimited = account.limits['resources'] === -1 || account.unlimited;

  return (
    <>
      {
        message?.isSuccess && (
          <div className='text-center p-4 mx-4 border border-green-500 rounded-sm bg-green-200'>
            { message.text }
          </div>
        )
      }
      {
        message && !message.isSuccess && (
          <div className='text-center p-4 mx-4 border border-red-500 rounded-sm bg-red-200'>
            { message.text }
          </div>
        )
      }
      <div>
        <h3 className='text-xl text-center font-bold my-4'>
          {account.email}
        </h3>
        <div className='flex justify-evenly mt-8'>
          <Button
            className='text-sm basis-1/4'
            grow={false}
            onClick={() => setActivePane('email')}
          >
            Change email
          </Button>
          <Button
            className='text-sm basis-1/4'
            grow={false}
            onClick={() => setActivePane('password')}
          >
            Change password
          </Button>
        </div>
      </div>

      <div className='grid grid-cols-[auto_1fr] gap-4 my-8 self-center'>
        {
          canSync && <SyncEnabled />
        }
        {
          !canSync && <SyncDisabled />
        }
        {
          unlimited && <AccountUnlimited />
        }
        {
          !unlimited && <AccountLimited resourceLimit={account.limits['resources']} />
        }
      </div>

      {
        (!unlimited || !canSync) && (
          <div className='bg-yellow-100 text-yellow-700 rounded-sm p-4 flex items-center'>
            <StarIcon className='size-8 mr-2' />
            <div>
              You can remove these account restrictions by subscribing to { ' ' }
              <b>JustWords Premium</b>.
            </div>
          </div>
        )
      }
    </>
  );
};
